/**
 * B2 upload flow — for files above Telegram's 50 MB bot API cap.
 *
 *   1. Ask the server for a presigned PUT URL (signed against our B2 bucket).
 *   2. PUT the file body directly to B2. This bypasses Cloudflare Workers'
 *      100 MB request-body cap entirely — the bytes go browser → B2 with
 *      nothing of ours in the path.
 *   3. Return a manifest entry tagged `storage: "b2"` so subsequent reads
 *      (preview, download, delete) route through the B2 proxy.
 */

function sanitizeFilename(name) {
  return (name || "file").replace(/[^\w.\- ]+/g, "_").slice(0, 200);
}

function buildB2Key(id, name) {
  return `${id}/${sanitizeFilename(name)}`;
}

async function getPresignedPutUrl(key, contentType) {
  const res = await fetch("/api/b2/presign-put", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, contentType }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) {
    throw new Error(json.error || `presign-put failed (${res.status})`);
  }
  return json.url;
}

export async function uploadFileToB2({ id, file, dims, onProgress }) {
  const contentType = file.type || "application/octet-stream";
  const key = buildB2Key(id, file.name);
  const url = await getPresignedPutUrl(key, contentType);

  // XHR is used here (not fetch) only because XHR exposes upload progress
  // events, which the skeleton tile uses to render a live progress bar.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);

    if (typeof onProgress === "function") {
      // Throttle: progress events can fire dozens of times per second on fast
      // uploads. Coalesce to ≥1% delta OR ≥200ms gap so React doesn't re-render
      // the skeleton tile on every byte.
      let lastPct = -1;
      let lastT = 0;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = e.loaded / e.total;
        const now = performance.now();
        if (pct - lastPct >= 0.01 || now - lastT >= 200 || pct >= 1) {
          onProgress(pct);
          lastPct = pct;
          lastT = now;
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === "function") onProgress(1);
        resolve();
      } else {
        reject(new Error(`B2 PUT failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("B2 PUT network error"));
    xhr.send(file);
  });

  return {
    id,
    name: file.name,
    mime: contentType,
    size: file.size,
    storage: "b2",
    b2Key: key,
    width: dims?.width,
    height: dims?.height,
    uploadedAt: new Date().toISOString(),
  };
}

export async function deleteFromB2(key) {
  if (!key) return;
  await fetch(`/api/b2/file?key=${encodeURIComponent(key)}`, { method: "DELETE" });
}
